import type { AppRouter } from "@server/routers/index";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import {
	createTRPCQueryUtils,
	createTRPCReact,
	httpBatchLink,
} from "@trpc/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { toast } from "sonner";
import { isAuthError, shouldRetry } from "./trpc-errors";

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Data stays fresh for 30s: navigating back to a page (or mounting a
			// second component that uses the same query) serves cache instantly
			// instead of re-hitting the worker. Explicit `staleTime: 0` on a query
			// still opts out where fresh reads are required (e.g. billing gates).
			staleTime: 30 * 1000,
			// Keep inactive data around long enough for back/forward navigation.
			gcTime: 10 * 60 * 1000,
			// Window focus refetch was disabled ad-hoc per query; make it the default.
			refetchOnWindowFocus: false,
			refetchOnReconnect: true,
			retry: shouldRetry,
		},
		mutations: {
			retry: false,
		},
	},
	queryCache: new QueryCache({
		onError: (error, query) => {
			// Queries that render their own error UI opt out via
			// `meta: { silentError: true }` so we don't double-report.
			if (query.meta?.silentError) return;

			if (isAuthError(error)) return;

			toast.error(error.message, {
				action: {
					label: "retry",
					onClick: () => {
						queryClient.invalidateQueries();
					},
				},
			});
		},
	}),
});

export const trpc = createTRPCReact<AppRouter>();

/** Inferred response types, e.g. `RouterOutputs["sites"]["getById"]`. */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export function createTrpcClient() {
	return trpc.createClient({
		links: [
			httpBatchLink({
				url: "/trpc",
				fetch(url, options) {
					return fetch(url, {
						...options,
						credentials: "include",
					});
				},
			}),
		],
	});
}

// Create and export a standalone client instance for direct query usage
export const trpcClient = createTrpcClient();

/**
 * Typed tRPC helpers usable outside React (route loaders, prefetch on intent).
 * Shares the same `queryClient` as the app, so anything fetched here is picked
 * up by `useQuery` in the component without a second request.
 */
export const queryUtils = createTRPCQueryUtils({
	client: trpcClient,
	queryClient,
});
